import React, { useState } from 'react';

import { ChevronDownIcon, ChevronUpIcon, Text, type TypeTextColor } from '@thefarmersfront/kitchen';

import clsx from 'clsx';

import { Bullet } from '~/components/common';

import styles from './Caution.module.scss';

const getBulletColor = (textColor: TypeTextColor): `#${string}` => {
  const colorMap: Partial<Record<TypeTextColor, `#${string}`>> = {
    secondary: '#848F9A',
    kurly: '#672091',
  };
  return colorMap[textColor] ?? '#848F9A';
};

type TypeCautionItem = {
  /** 내용 */
  children: React.ReactNode;
};

type TypeCautionProps = {
  /** 타이틀 */
  title?: React.ReactNode;
  /** 내용 목록 */
  contents: TypeCautionItem[];
  /**
   * 내용 bullet 노출 여부
   * @default false
   */
  showBullet?: boolean;
  /**
   * 커스텀 색상 설정
   */
  color?: {
    /**
     * 제목 색상
     * @default 'kurly'
     */
    titleColor?: TypeTextColor;
    /**
     * Chevron 아이콘 색상
     * @note #으로 시작하는 색상 해시값 입력
     * @default '#BD76FF'
     */
    chevronColor?: `#${string}`;
    /**
     * 본문 및 bullet 색상
     * @default 'secondary'
     */
    contentColor?: TypeTextColor;
    /**
     * 배경색
     * @default 'point'
     */
    backgroundColor?:
      | 'primary'
      | 'secondary'
      | 'tertiary'
      | 'quaternary'
      | 'quinary'
      | 'kurly'
      | 'info'
      | 'success'
      | 'warning'
      | 'error'
      | 'point';
  };
};

const Caution = ({ title, contents, color, showBullet = false }: TypeCautionProps) => {
  const {
    titleColor = 'kurly',
    chevronColor = '#BD76FF',
    contentColor = 'secondary',
    backgroundColor = 'point',
  } = { ...color };

  const collapsible = !!title;

  const [isOpen, setIsOpen] = useState(!collapsible);

  const handleToggle = () => {
    if (collapsible) {
      setIsOpen((prev) => !prev);
    }
  };

  const bulletColor = getBulletColor(contentColor);

  return (
    <div className={clsx(styles['caution-container'], backgroundColor && styles[`bg-${backgroundColor}`])}>
      {title && (
        <button className={styles['caution-title']} onClick={handleToggle}>
          <Text size="xxlarge" weight="bold" color={titleColor}>
            {title}
          </Text>
          {isOpen ? <ChevronUpIcon color={chevronColor} /> : <ChevronDownIcon color={chevronColor} />}
        </button>
      )}
      {isOpen && (
        <div className={clsx(styles['caution-contents'], collapsible && styles['collapsible'])}>
          {contents.map((content, index) => {
            const contentKey =
              typeof content.children === 'string'
                ? content.children.replace(/\s+/g, '-').toLowerCase()
                : `content-${index}`;

            return (
              <div key={contentKey} className={styles['caution-content-item']}>
                {showBullet && <Bullet fill={bulletColor} />}
                <Text className={styles['caution-content-text']} size={title ? 'medium' : 'large'} color={contentColor}>
                  {content.children}
                </Text>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Caution;
